import React, { useState, useEffect, useCallback } from 'react';
import { WifiOff, Wifi, Upload, RefreshCw, CheckCircle, ChevronDown, ChevronUp, Clock, AlertTriangle } from 'lucide-react';
import { onSyncUpdate, syncPendingEntries, getPendingCount } from '../utils/offlineSync';
import { offlineDB } from '../utils/offlineDB';
import { toast } from 'sonner';
import { format } from 'date-fns';

const MODULE_LABELS = {
  maintenance: 'Maintenance Log',
  production: 'Production Trip',
  diesel: 'Diesel Entry',
  hour_meter: 'Hour Meter',
  task: 'Task',
  parts_release: 'Parts Release',
  grn: 'Goods Received Note',
};

const OfflineBanner = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logEntries, setLogEntries] = useState([]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const unsub = onSyncUpdate((count) => setPendingCount(count));
    getPendingCount().then(setPendingCount);
    return unsub;
  }, []);

  // Expose setPendingCount for useOfflineForm hook
  useEffect(() => {
    window.__setOfflinePendingCount = setPendingCount;
    return () => { delete window.__setOfflinePendingCount; };
  }, []);

  const loadLog = useCallback(async () => {
    const entries = await offlineDB.getAll(20);
    setLogEntries(entries);
  }, []);

  const toggleLog = useCallback(async () => {
    if (!showLog) await loadLog();
    setShowLog((v) => !v);
  }, [showLog, loadLog]);

  const handleManualSync = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error('No internet connection');
      return;
    }
    setSyncing(true);
    const result = await syncPendingEntries();
    setSyncing(false);
    if (result.synced > 0) {
      toast.success(`Synced ${result.synced} entr${result.synced === 1 ? 'y' : 'ies'}`);
      window.dispatchEvent(new CustomEvent('offlineSyncComplete', { detail: result }));
    }
    if (result.failed > 0) toast.error(`${result.failed} entries failed to sync`);
    await loadLog();
  }, [loadLog]);

  const formatDate = (iso) => {
    try { return format(new Date(iso), 'dd MMM HH:mm'); } catch { return '—'; }
  };

  // Don't render anything if online and no pending items
  if (isOnline && pendingCount === 0 && !showLog) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full" data-testid="offline-banner">
      {/* Main Status Pill */}
      <div
        className={`flex items-center justify-between rounded-xl px-4 py-3 shadow-xl cursor-pointer border transition-all ${
          !isOnline
            ? 'bg-red-900 border-red-700 text-white'
            : pendingCount > 0
            ? 'bg-amber-900 border-amber-700 text-white'
            : 'bg-green-800 border-green-700 text-white'
        }`}
        onClick={toggleLog}
        data-testid="offline-pill"
      >
        <div className="flex items-center gap-2">
          {!isOnline ? (
            <WifiOff className="w-4 h-4 text-red-300 flex-shrink-0" />
          ) : syncing ? (
            <RefreshCw className="w-4 h-4 text-amber-300 animate-spin flex-shrink-0" />
          ) : pendingCount > 0 ? (
            <Upload className="w-4 h-4 text-amber-300 flex-shrink-0" />
          ) : (
            <Wifi className="w-4 h-4 text-green-300 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">
            {!isOnline
              ? 'No connection — saving locally'
              : syncing
              ? `Syncing ${pendingCount} entr${pendingCount === 1 ? 'y' : 'ies'}…`
              : pendingCount > 0
              ? `${pendingCount} entr${pendingCount === 1 ? 'y' : 'ies'} pending upload`
              : 'All synced'}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-3">
          {isOnline && pendingCount > 0 && !syncing && (
            <button
              onClick={(e) => { e.stopPropagation(); handleManualSync(); }}
              className="text-xs bg-amber-700 hover:bg-amber-600 px-2 py-1 rounded-lg font-medium transition-colors"
              data-testid="manual-sync-btn"
            >
              Sync now
            </button>
          )}
          {showLog ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronUp className="w-4 h-4 opacity-60" />}
        </div>
      </div>

      {/* Sync Log Panel */}
      {showLog && (
        <div className="mt-2 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden" data-testid="sync-log-panel">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
            <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Offline Queue</span>
            <button
              onClick={(e) => { e.stopPropagation(); loadLog(); }}
              className="text-slate-400 hover:text-slate-200"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {logEntries.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No offline entries</p>
            ) : (
              logEntries.map((entry) => (
                <div
                  key={entry.offlineId}
                  className="flex items-start gap-2 px-4 py-2 border-b border-slate-800 last:border-0"
                  data-testid={`sync-entry-${entry.offlineId}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {entry.status === 'synced' ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                    ) : entry.status === 'failed' ? (
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                    ) : (
                      <Clock className="w-3.5 h-3.5 text-amber-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-200 truncate">
                      {entry.displayName || MODULE_LABELS[entry.module] || entry.module}
                    </p>
                    <p className="text-xs text-slate-400">{formatDate(entry.created_at)}</p>
                    {entry.status === 'failed' && entry.last_error && (
                      <p className="text-xs text-red-400 truncate">{entry.last_error}</p>
                    )}
                  </div>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                      entry.status === 'synced'
                        ? 'bg-green-900 text-green-300'
                        : entry.status === 'failed'
                        ? 'bg-red-900 text-red-300'
                        : 'bg-amber-900 text-amber-300'
                    }`}
                  >
                    {entry.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineBanner;
